<script>
  import {
    computed,
    ref,
    onMounted,
    onBeforeUnmount,
  } from "@nuxtjs/composition-api";
  import CollapsedContainer from "@/components/Ecommerce/CollapsedContainer";

  export default {
    components: {
      CollapsedContainer,
    },
    props: {
      attributes: {
        type: Array,
        default: () => [],
      },
    },
    setup (props) {
      const isDesktop = ref(false);

      const checkScreenSize = () => {
        isDesktop.value = window.innerWidth >= 768; // md breakpoint
      };

      onMounted(() => {
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);
      });

      onBeforeUnmount(() => {
        window.removeEventListener("resize", checkScreenSize);
      });

      const attributesToHide = [
        "EXAMEN_BOLCOM",
        "EXAMEN_BRUNA",
        "EXAMEN_DIJK",
        "EXAMEN_INKIJKEXEMPLAAR",
        "EXAMEN_DATUM_EERSTE_DRUK",
      ];

      const filteredAttributes = computed(() => {
        const attributes = props.attributes.filter(
          (attribute) =>
            !attributesToHide.includes(attribute.name) &&
            !attribute.name.startsWith("TM_")
        );
        const cleanedAttributes = attributes.filter(
          (attribute) => attribute.value
        );
        return cleanedAttributes;
      });

      const shouldShowCollapsed = computed(() => {
        return !isDesktop.value && filteredAttributes.value.length > 6;
      });

      const productType = computed(() => {
        const typeAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_MERK"
        );
        return typeAttribute ? typeAttribute : null;
      });

      const subjectType = computed(() => {
        const subjectAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_SCHOOLVAK"
        );
        return subjectAttribute ? subjectAttribute : null;
      });

      const level = computed(() => {
        const levelAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_ONDERWIJSTYPE"
        );
        return levelAttribute ? levelAttribute : null;
      });

      const pages = computed(() => {
        const pagesAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_PAGINAS"
        );
        return pagesAttribute ? pagesAttribute : null;
      });

      const editions = computed(() => {
        const editionAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_EDITIE"
        );
        return editionAttribute ? editionAttribute : null;
      });

      const size = computed(() => {
        const sizeAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_AFMETINGEN"
        );
        return sizeAttribute ? sizeAttribute : null;
      });

      const writers = computed(() => {
        const writersAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_AUTEURS"
        );
        return writersAttribute ? writersAttribute : null;
      });

      const ISBN = computed(() => {
        const isbnAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_ISBN"
        );
        return isbnAttribute ? isbnAttribute : null;
      });

      const learningEnvironment = computed(() => {
        const learningEnvAttribute = props.attributes.find(
          (attribute) => attribute.name === "EXAMEN_ONLINE_LEEROMGEVING"
        );
        // The backend serves no localized NL description for this attribute,
        // so its label falls back to the raw key. Override with a human label.
        return learningEnvAttribute
          ? { ...learningEnvAttribute, label: "Online leeromgeving" }
          : null;
      });

      const sortedAttributes = computed(() => {
        const attributesList = [
          productType.value,
          subjectType.value,
          level.value,
          pages.value,
          editions.value,
          size.value,
          writers.value,
          ISBN.value,
          learningEnvironment.value,
        ].filter((attribute) => !!attribute?.value);
        return attributesList;
      });

      return {
        filteredAttributes,
        shouldShowCollapsed,
        sortedAttributes,
      };
    },
  };
</script>

<template>
  <div class="pb-8">
    <h3 class="mt-9 md:mt-0 text-1xl mb-6">
      Productspecificaties
    </h3>
    <CollapsedContainer :show-collapsed="shouldShowCollapsed">
      <table>
        <tbody>
          <tr
            v-for="(attribute, key) in sortedAttributes"
            :key="key"
          >
            <td>{{ attribute.label }}</td>
            <td>{{ attribute.value }}</td>
          </tr>
        </tbody>
      </table>
    </CollapsedContainer>
  </div>
</template>

<style scoped lang="scss">
table {
  @apply w-full;
  tr {
    @apply border-t border-b border-gray-100;
    td {
      @apply py-3 leading-none;
    }
  }
}
</style>
